import { apiClient } from '@/app/core/services/apiClient.service';
import { BaseApiService } from '@/app/shared/services/apis/baseApi.service';
import type {
    CreateParticipantPayload,
    ExportGuestListToXLSXPayload,
    GuestListResponse,
    SetTicketsCapacityPayload,
    UpdateParticipantCollectingPointPayload,
    UpdateParticipantsTicketsPayload,
    BlockedForOccasionGuestDetails,
    BulkUpdateParticipantsTicketsPayload,
} from '@/app/shared/services/apis/types/guestList/guestList';
import type { ID } from '@fifa-bsm/bo-frontend-shared/utils/types';
import type { ApiRequestConfig, QueryParams } from '@fifa-bsm/bo-frontend-shared';
import type {
    GroupDashboardEvent,
    GroupDashboardFilters,
    ParticipantGroupDashboardUpdatePayload,
    SeatGroupStatistics,
    UpdateGroupDashboardSeatGroupCapacitiesPayload,
} from '@/app/shared/services/apis/types/guestList/groupDashboard';
import type { ApiResponse } from '@/app/shared/services/apis/types/ApiResponse';
import type { AxiosResponse } from 'axios';
import type { SetToWaitingListPayload } from '@/app/shared/services/apis/types/eTicketing/payloads.ts';

class GuestListApiService extends BaseApiService {
    getMainEvents(params: QueryParams) {
        return this.client.get(`guest-list/main-events`, params);
    }

    getGuestList(
        mainEventId: ID,
        eventId: ID,
        queryParams?: Record<any, any>
    ): Promise<GuestListResponse> {
        return this.client.get<GuestListResponse>(
            `guest-list/main-events/${mainEventId}/events/${eventId}/participants`,
            queryParams
        );
    }

    createParticipant(mainEventId: ID, eventId: ID, payload: CreateParticipantPayload) {
        return this.client.post<void>(
            `guest-list/main-events/${mainEventId}/events/${eventId}/participants`,
            payload
        );
    }

    updateTickets(
        mainEventId: ID,
        eventId: ID,
        payload: UpdateParticipantsTicketsPayload,
        config?: ApiRequestConfig<any, any>
    ) {
        return this.client.patch<{ mismatch: boolean }>(
            `guest-list/main-events/${mainEventId}/events/${eventId}/participants/tickets`,
            payload,
            config
        );
    }

    updateTicketsInBulk(
        mainEventId: ID,
        payload: BulkUpdateParticipantsTicketsPayload,
        config?: ApiRequestConfig<any, any>
    ) {
        return this.client.patch<ApiResponse<{ batchId: string }>>(
            `guest-list/main-events/${mainEventId}/participants/tickets/bulk-update-amounts`,
            payload,
            config
        );
    }

    setCapacity(mainEventId: ID, eventId: ID, payload: SetTicketsCapacityPayload) {
        return this.client.post<void>(
            `guest-list/main-events/${mainEventId}/events/${eventId}/set-tickets-capacity`,
            payload
        );
    }

    setCollectionPoints(
        mainEventId: ID,
        eventId: ID,
        payload: UpdateParticipantCollectingPointPayload
    ) {
        return this.client.patch<void>(
            `guest-list/main-events/${mainEventId}/events/${eventId}/participants/collecting-points`,
            payload
        );
    }

    async dismissInconsistencies(mainEventId: ID, eventId: ID, occasionId: ID) {
        return this.client.patch<void>(
            `main-events/${mainEventId}/events/${eventId}/occasions/${occasionId}/dismiss-inconsistencies`
        );
    }

    async overwriteInconsistencies(mainEventId: ID, eventId: ID, occasionId: ID) {
        return this.client.patch<void>(
            `main-events/${mainEventId}/events/${eventId}/occasions/${occasionId}/overwrite-inconsistencies`
        );
    }

    async exportGuestListToXLSX(
        mainEventId: ID,
        eventId: ID,
        payload: ExportGuestListToXLSXPayload
    ): Promise<AxiosResponse<ArrayBuffer>> {
        return this.client.post<AxiosResponse<ArrayBuffer>>(
            `guest-list/main-events/${mainEventId}/events/${eventId}/export`,
            payload,
            {
                mapResponse: (response: AxiosResponse<ArrayBuffer>) => response,
                responseType: 'arraybuffer',
            }
        );
    }

    async exportWaitingListToXLS(
        mainEventId: ID,
        eventId: ID
    ): Promise<AxiosResponse<ArrayBuffer>> {
        return this.client.get<AxiosResponse<ArrayBuffer>>(
            `main-events/${mainEventId}/events/${eventId}/tickets/waiting-list-report`,
            {},
            {
                mapResponse: (response: any) => response,
                responseType: 'arraybuffer',
            }
        );
    }

    async exportCancelledTicketsToXLS(
        mainEventId: ID,
        eventId: ID
    ): Promise<AxiosResponse<ArrayBuffer>> {
        return this.client.get<AxiosResponse<ArrayBuffer>>(
            `main-events/${mainEventId}/reports/ticket-cancellation/${eventId}`,
            {},
            {
                mapResponse: (response: any) => response,
                responseType: 'arraybuffer',
            }
        );
    }

    getGroupDashboard(
        mainEventId: ID,
        queryParams?: QueryParams,
        config?: ApiRequestConfig<any, any>
    ) {
        return this.client.get<ApiResponse<GroupDashboardEvent[]>>(
            `main-events/${mainEventId}/group-dashboard`,
            queryParams,
            config
        );
    }

    getGroupDashboardFilters(mainEventId: ID) {
        return this.client.get<ApiResponse<GroupDashboardFilters>>(
            `main-events/${mainEventId}/group-dashboard/filters`
        );
    }

    editParticipantGroupDashboardForEvent(
        mainEventId: ID,
        eventId: ID,
        participantGroupId: ID,
        payload: ParticipantGroupDashboardUpdatePayload
    ) {
        return this.client.patch(
            `main-events/${mainEventId}/events/${eventId}/participant-groups/${participantGroupId}/update-group-dashboard`,
            payload
        );
    }

    getGroupDashboardSeatGroupCapacities(
        mainEventId: ID,
        queryParams: QueryParams,
        config?: ApiRequestConfig<
            ApiResponse<SeatGroupStatistics>,
            ApiResponse<SeatGroupStatistics>
        >
    ) {
        return this.client.get<ApiResponse<SeatGroupStatistics>>(
            `main-events/${mainEventId}/group-dashboard/seat-groups-statistics`,
            queryParams,
            config
        );
    }

    updateGroupDashboardSeatGroupCapacities(
        mainEventId: ID,
        payload: UpdateGroupDashboardSeatGroupCapacitiesPayload,
        config?: ApiRequestConfig<void, void>
    ) {
        return this.client.patch<void>(
            `main-events/${mainEventId}/group-dashboard/seat-group-capacities`,
            payload,
            config
        );
    }

    exportGroupDashboard(
        mainEventId: ID,
        payload: { event_ids: number[] },
        config?: ApiRequestConfig<
            AxiosResponse<ArrayBuffer>,
            AxiosResponse<ArrayBuffer>,
            AxiosResponse<ArrayBuffer>
        >
    ) {
        return this.client.post(`main-events/${mainEventId}/group-dashboard/export`, payload, {
            ...config,
            mapResponse: (response) => response,
            responseType: 'arraybuffer',
        });
    }

    async addParticipantGroupToGroupDashboard(mainEventId: ID, participantGroupId: ID) {
        return this.client.post(
            `main-events/${mainEventId}/group-dashboard/participant-groups/${participantGroupId}/add`
        );
    }

    toggleInitialInformationNotifications(
        mainEventId: ID,
        payload: {
            guest_ids: ID[];
            all_events: boolean;
            block: boolean;
            event_id: ID | null;
        }
    ) {
        return this.client.post(
            `main-events/${mainEventId}/toggle-initial-information-notifications`,
            payload
        );
    }

    eventsBlockedFromInformation(mainEventId: ID, guestOperationsIndividualId: ID) {
        return this.client.get<
            ApiResponse<{
                guest: BlockedForOccasionGuestDetails;
                events: GroupDashboardEvent[];
            }>
        >(
            `main-events/${mainEventId}/guest-operations-individuals/${guestOperationsIndividualId}/events-blocked-from-information`
        );
    }

    setToWaitingList(mainEventId: ID, eventId: ID, payload: SetToWaitingListPayload) {
        return this.client.patch(
            `guest-list/main-events/${mainEventId}/events/${eventId}/participants/tickets/waiting-list/set`,
            payload
        );
    }
}

export const guestListApi = new GuestListApiService(apiClient);
